/**
 * Szintetizált WebAudio hangmotor. Nincs külső hangfájl, nincs dependency.
 * Fontos: az AudioContext csak felhasználói gesztus után jön létre, és minden
 * nyilvános metódus hiba esetén csendben letiltja magát (soha nem dob).
 */

export type SfxName =
  | 'ui'
  | 'select'
  | 'pickup'
  | 'roll'
  | 'use'
  | 'hit'
  | 'block'
  | 'boost'
  | 'lap'
  | 'finish'
  | 'countdown'
  | 'go'
  | 'bump';

export interface EngineVoice {
  speed: number;
  maxSpeed: number;
  throttle: number;
  active: boolean;
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

interface EngineNode {
  oscA: OscillatorNode;
  oscB: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
}

interface ToneOptions {
  freq: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  when?: number;
  slideTo?: number;
  filter?: number;
  filterType?: BiquadFilterType;
  filterTo?: number;
}

interface NoiseOptions {
  dur: number;
  gain?: number;
  when?: number;
  filter?: number;
  filterTo?: number;
  type?: BiquadFilterType;
  q?: number;
}

const MASTER_GAIN = 0.62;
const ENGINE_BUS_GAIN = 0.5;
const SFX_BUS_GAIN = 0.55;
const MUSIC_BUS_GAIN = 0.5;
const MAX_VOICES = 3;
const SILENT = 0.0001;
const MUSIC_KEY = 'csf.music.v1';
const SFX_KEY = 'csf.sfx.v1';

type MusicKind = 'menu' | 'race';

/** Négyütemes akkordmenet (A-moll, F, C, G) midi gyökerekkel. */
const MENU_CHORDS = [57, 53, 48, 55];
const RACE_BASS = [45, 45, 57, 45, 52, 45, 50, 45];
const RACE_LEAD = [69, 72, 76, 72, 69, 67, 64, 67];

const midiToFreq = (midi: number): number => 440 * Math.pow(2, (midi - 69) / 12);

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** A böngésző AudioContext konstruktora (webkit fallback). */
function getAudioCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** A zene be/ki állapota (localStorage, alapból be). */
function readMusicPref(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(MUSIC_KEY) !== '0';
  } catch {
    return true;
  }
}

/** A hangeffektek be/ki állapota (localStorage, alapból be). */
function readSfxPref(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(SFX_KEY) !== '0';
  } catch {
    return true;
  }
}

/** Rövid, halkan szóló szintetizált effekt- és motorhangmotor. */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private readonly voices: EngineNode[] = [];
  private starting: Promise<void> | null = null;
  private ready = false;
  private _muted = false;

  private musicBus: GainNode | null = null;
  private musicKind: MusicKind | null = null;
  private musicOn = readMusicPref();
  private sfxOn = readSfxPref();
  private musicTimer = 0;
  private musicStep = 0;
  private musicNext = 0;

  get muted(): boolean {
    return this._muted;
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    if (!this.ctx || !this.master) return;
    try {
      const now = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setTargetAtTime(muted ? 0 : MASTER_GAIN, now, 0.03);
    } catch {
      // csendben: a hang nem kritikus
    }
  }

  toggleMute(): void {
    this.setMuted(!this._muted);
  }

  /** Felhasználói gesztus után hívd; többször hívható, nem dob. */
  start(): Promise<void> {
    if (this.starting) return this.starting;
    const boot = this.boot();
    this.starting = boot;
    return boot;
  }

  private async boot(): Promise<void> {
    try {
      const Ctor = getAudioCtor();
      if (!Ctor) return;

      if (!this.ctx) {
        const ctx = new Ctor();
        const master = ctx.createGain();
        master.gain.value = this._muted ? 0 : MASTER_GAIN;
        master.connect(ctx.destination);

        const engineBus = ctx.createGain();
        engineBus.gain.value = ENGINE_BUS_GAIN;
        engineBus.connect(master);

        const sfxBus = ctx.createGain();
        sfxBus.gain.value = this.sfxOn ? SFX_BUS_GAIN : 0;
        sfxBus.connect(master);

        const musicBus = ctx.createGain();
        musicBus.gain.value = MUSIC_BUS_GAIN;
        musicBus.connect(master);

        this.ctx = ctx;
        this.master = master;
        this.engineBus = engineBus;
        this.sfxBus = sfxBus;
        this.musicBus = musicBus;
        this.noiseBuffer = this.makeNoise(ctx);
        this.createVoices(ctx, engineBus);
      }

      await this.resume();
      this.ready = true;
      if (this.musicKind && this.musicOn) this.startMusicLoop();
    } catch {
      this.ready = false;
    }
  }

  private async resume(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'running') return;
    try {
      await ctx.resume();
    } catch {
      // autoplay: marad csendben
    }
  }

  /** 0,5 mp-es determinisztikus fehér zaj pufferekhez. */
  private makeNoise(ctx: AudioContext): AudioBuffer {
    const length = Math.max(1, Math.floor(ctx.sampleRate * 0.5));
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let seed = 0x1a2b3c4d;
    for (let i = 0; i < length; i++) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      data[i] = (seed / 0xffffffff) * 2 - 1;
    }
    return buffer;
  }

  private createVoices(ctx: AudioContext, bus: GainNode): void {
    for (let i = 0; i < MAX_VOICES; i++) {
      const oscA = ctx.createOscillator();
      oscA.type = 'sawtooth';
      oscA.frequency.value = 52 + i * 3;

      const oscB = ctx.createOscillator();
      oscB.type = 'triangle';
      oscB.frequency.value = (52 + i * 3) * 0.5;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 320;
      filter.Q.value = 1.5;

      const gain = ctx.createGain();
      gain.gain.value = 0;

      oscA.connect(filter);
      oscB.connect(filter);
      filter.connect(gain);
      gain.connect(bus);

      oscA.start();
      oscB.start();
      this.voices.push({ oscA, oscB, filter, gain });
    }
  }

  /** Legfeljebb 3 motorhang, halkan; a hívó játékosonként adhat egyet. */
  updateEngines(engines: EngineVoice[]): void {
    if (!this.ready || !this.ctx) return;
    try {
      const now = this.ctx.currentTime;
      let cursor = 0;

      for (let i = 0; i < this.voices.length; i++) {
        const voice = this.voices[i];
        let source: EngineVoice | null = null;
        while (cursor < engines.length) {
          const candidate = engines[cursor];
          cursor++;
          if (candidate.active) {
            source = candidate;
            break;
          }
        }

        if (!source) {
          voice.gain.gain.setTargetAtTime(SILENT, now, 0.12);
          voice.filter.frequency.setTargetAtTime(240, now, 0.2);
          continue;
        }

        const speedNorm = clamp(source.speed / Math.max(1, source.maxSpeed), 0, 1.4);
        const throttle = clamp(source.throttle, 0, 1);
        const wobble = Math.sin(now * 6.5 + i * 1.7) * 1.5;
        const base = 50 + speedNorm * 140 + throttle * 18 + i * 2.5 + wobble;

        voice.oscA.frequency.setTargetAtTime(base, now, 0.09);
        voice.oscB.frequency.setTargetAtTime(base * 0.5, now, 0.11);
        voice.filter.frequency.setTargetAtTime(230 + speedNorm * 1450 + throttle * 320, now, 0.12);

        const volume = 0.028 + throttle * 0.038 + speedNorm * 0.042;
        voice.gain.gain.setTargetAtTime(volume, now, 0.14);
      }
    } catch {
      // csendben
    }
  }

  /** Minden motorhang azonnali elhalkítása (menübe lépéskor, szünetnél). */
  stopEngines(): void {
    if (!this.ready || !this.ctx) return;
    try {
      const now = this.ctx.currentTime;
      for (const voice of this.voices) {
        voice.gain.gain.cancelScheduledValues(now);
        voice.gain.gain.setTargetAtTime(SILENT, now, 0.06);
        voice.filter.frequency.setTargetAtTime(240, now, 0.1);
      }
    } catch {
      // csendben
    }
  }

  get musicEnabled(): boolean {
    return this.musicOn;
  }

  setMusicEnabled(on: boolean): void {
    this.musicOn = on;
    try {
      window.localStorage.setItem(MUSIC_KEY, on ? '1' : '0');
    } catch {
      // privát mód
    }
    if (!on) this.stopMusicLoop();
    else if (this.musicKind && this.ready) this.startMusicLoop();
  }

  toggleMusic(): boolean {
    this.setMusicEnabled(!this.musicOn);
    return this.musicOn;
  }

  get sfxEnabled(): boolean {
    return this.sfxOn;
  }

  setSfxEnabled(on: boolean): void {
    this.sfxOn = on;
    try {
      window.localStorage.setItem(SFX_KEY, on ? '1' : '0');
    } catch {
      // privát mód
    }
    if (!this.ctx || !this.sfxBus) return;
    try {
      this.sfxBus.gain.setTargetAtTime(on ? SFX_BUS_GAIN : 0, this.ctx.currentTime, 0.03);
    } catch {
      // csendben
    }
  }

  toggleSfx(): boolean {
    this.setSfxEnabled(!this.sfxOn);
    return this.sfxOn;
  }

  /** Menü- vagy versenyzene; null = nincs. */
  setMusic(kind: MusicKind | null): void {
    if (this.musicKind === kind) return;
    this.musicKind = kind;
    if (kind === null) {
      this.stopMusicLoop();
      return;
    }
    if (this.ready && this.musicOn) this.startMusicLoop();
  }

  private startMusicLoop(): void {
    if (!this.ctx || !this.musicKind || !this.musicBus) return;
    this.stopMusicLoop();
    this.musicStep = 0;
    this.musicNext = this.ctx.currentTime + 0.12;
    this.musicTimer = window.setInterval(() => this.scheduleMusic(), 80);
  }

  private stopMusicLoop(): void {
    if (!this.musicTimer) return;
    window.clearInterval(this.musicTimer);
    this.musicTimer = 0;
  }

  private scheduleMusic(): void {
    const ctx = this.ctx;
    const kind = this.musicKind;
    if (!ctx || !kind || !this.musicBus || !this.ready) return;
    const lookahead = ctx.currentTime + 0.4;
    const stepDur = kind === 'menu' ? 0.2 : 0.14;
    let guard = 0;
    while (this.musicNext < lookahead && guard++ < 16) {
      this.playMusicStep(kind, this.musicStep, this.musicNext, stepDur);
      this.musicNext += stepDur;
      this.musicStep = (this.musicStep + 1) % 64;
    }
  }

  private playMusicStep(kind: MusicKind, step: number, at: number, stepDur: number): void {
    const bar = Math.floor(step / 16);
    const local = step % 16;

    if (kind === 'menu') {
      const root = MENU_CHORDS[bar];
      if (local === 0) {
        this.musicTone(root + 12, at, 3.1, 'triangle', 0.05);
        this.musicTone(root - 12, at, 1.4, 'sine', 0.09);
      }
      if (local === 8) this.musicTone(root - 5, at, 1.4, 'sine', 0.06);
      if (local % 2 === 0) {
        const arp = [0, 7, 12, 7, 4, 7, 12, 15][Math.floor(local / 2)];
        this.musicTone(root + 12 + arp, at, stepDur * 1.6, 'square', 0.022);
      }
      if (local % 4 === 2) this.musicHat(at, 0.012);
      return;
    }

    if (local % 2 === 0) {
      const idx = (step / 2) % 8;
      this.musicTone(RACE_BASS[idx], at, stepDur * 1.7, 'triangle', 0.08);
      this.musicTone(RACE_LEAD[idx], at, stepDur * 1.2, 'square', 0.024);
      this.musicHat(at, 0.016);
      if (local % 4 === 0) this.musicHat(at + stepDur, 0.008);
    }
  }

  private musicTone(midi: number, at: number, dur: number, type: OscillatorType, gain: number): void {
    const ctx = this.ctx;
    const bus = this.musicBus;
    if (!ctx || !bus) return;
    try {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(midiToFreq(midi), at);
      const env = ctx.createGain();
      env.gain.setValueAtTime(SILENT, at);
      env.gain.exponentialRampToValueAtTime(Math.max(SILENT, gain), at + 0.012);
      env.gain.exponentialRampToValueAtTime(SILENT, at + Math.max(0.05, dur));
      osc.connect(env);
      env.connect(bus);
      osc.start(at);
      osc.stop(at + Math.max(0.05, dur) + 0.03);
    } catch {
      // csendben
    }
  }

  private musicHat(at: number, gain: number): void {
    const ctx = this.ctx;
    const bus = this.musicBus;
    if (!ctx || !bus || !this.noiseBuffer) return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 6200;
      const env = ctx.createGain();
      env.gain.setValueAtTime(Math.max(SILENT, gain), at);
      env.gain.exponentialRampToValueAtTime(SILENT, at + 0.05);
      src.connect(filter);
      filter.connect(env);
      env.connect(bus);
      src.start(at);
      src.stop(at + 0.06);
    } catch {
      // csendben
    }
  }

  sfx(name: SfxName): void {    if (!this.ready || !this.ctx || !this.sfxBus || this._muted) return;
    try {
      switch (name) {
        case 'ui':
          this.tone({ freq: 560, slideTo: 500, dur: 0.05, type: 'triangle', gain: 0.08 });
          break;
        case 'select':
          this.tone({ freq: 640, slideTo: 780, dur: 0.07, type: 'triangle', gain: 0.1 });
          this.tone({ freq: 960, dur: 0.08, type: 'triangle', gain: 0.06, when: 0.05 });
          break;
        case 'pickup':
          this.tone({ freq: 660, dur: 0.1, type: 'triangle', gain: 0.09 });
          this.tone({ freq: 880, dur: 0.1, type: 'triangle', gain: 0.08, when: 0.045 });
          this.tone({ freq: 1180, dur: 0.12, type: 'triangle', gain: 0.07, when: 0.09 });
          break;
        case 'roll':
          this.tone({ freq: 240, slideTo: 900, dur: 0.24, type: 'square', gain: 0.05 });
          this.noise({ dur: 0.2, gain: 0.035, filter: 600, filterTo: 2200, type: 'bandpass', q: 1.4 });
          break;
        case 'use':
          this.noise({ dur: 0.18, gain: 0.12, filter: 420, filterTo: 1900 });
          this.tone({ freq: 220, slideTo: 620, dur: 0.2, type: 'sawtooth', gain: 0.05 });
          break;
        case 'hit':
          this.noise({ dur: 0.16, gain: 0.18, filter: 1700, filterTo: 220 });
          this.tone({ freq: 110, slideTo: 45, dur: 0.24, type: 'square', gain: 0.1 });
          break;
        case 'bump':
          this.noise({ dur: 0.09, gain: 0.1, filter: 900, filterTo: 300, type: 'lowpass' });
          this.tone({ freq: 95, slideTo: 60, dur: 0.12, type: 'sine', gain: 0.07 });
          break;
        case 'block':
          this.tone({ freq: 1450, dur: 0.1, type: 'sine', gain: 0.09 });
          this.tone({ freq: 2150, dur: 0.12, type: 'sine', gain: 0.055, when: 0.02 });
          break;
        case 'boost':
          this.noise({ dur: 0.35, gain: 0.1, filter: 320, filterTo: 2600, type: 'bandpass', q: 0.8 });
          this.tone({ freq: 160, slideTo: 760, dur: 0.38, type: 'sawtooth', gain: 0.05 });
          break;
        case 'lap':
          this.tone({ freq: 660, dur: 0.11, type: 'triangle', gain: 0.1 });
          this.tone({ freq: 990, dur: 0.14, type: 'triangle', gain: 0.08, when: 0.1 });
          break;
        case 'finish':
          this.tone({ freq: 523.25, dur: 0.18, type: 'triangle', gain: 0.11 });
          this.tone({ freq: 659.25, dur: 0.18, type: 'triangle', gain: 0.1, when: 0.11 });
          this.tone({ freq: 783.99, dur: 0.18, type: 'triangle', gain: 0.1, when: 0.22 });
          this.tone({ freq: 1046.5, dur: 0.3, type: 'triangle', gain: 0.11, when: 0.33 });
          this.tone({ freq: 1568, dur: 0.3, type: 'sine', gain: 0.05, when: 0.5 });
          break;
        case 'countdown':
          this.tone({ freq: 520, dur: 0.12, type: 'square', gain: 0.07 });
          break;
        case 'go':
          this.tone({ freq: 880, dur: 0.22, type: 'square', gain: 0.09 });
          this.tone({ freq: 1320, dur: 0.3, type: 'triangle', gain: 0.07, when: 0.03 });
          break;
        default:
          break;
      }
    } catch {
      // csendben
    }
  }

  /** 3, 2, 1, 0 (0 = GO). */
  countdown(n: number): void {
    if (!this.ready || this._muted) return;
    try {
      if (n <= 0) {
        this.sfx('go');
        return;
      }
      const step = Math.min(3, Math.max(1, Math.round(n)));
      this.tone({ freq: 500 + (3 - step) * 55, dur: 0.13, type: 'square', gain: 0.075 });
    } catch {
      // csendben
    }
  }

  private tone(opts: ToneOptions): void {
    const ctx = this.ctx;
    const bus = this.sfxBus;
    if (!ctx || !bus) return;

    const t0 = ctx.currentTime + (opts.when ?? 0);
    const dur = Math.max(0.02, opts.dur);
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? 'triangle';
    const f0 = Math.max(20, opts.freq);
    osc.frequency.setValueAtTime(f0, t0);
    if (opts.slideTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.slideTo), t0 + dur);
    }

    const gain = ctx.createGain();
    const peak = Math.max(SILENT, opts.gain ?? 0.15);
    gain.gain.setValueAtTime(SILENT, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.012, dur * 0.25));
    gain.gain.exponentialRampToValueAtTime(SILENT, t0 + dur);

    let node: AudioNode = osc;
    if (opts.filter !== undefined) {
      const filter = ctx.createBiquadFilter();
      filter.type = opts.filterType ?? 'lowpass';
      filter.frequency.setValueAtTime(Math.max(40, opts.filter), t0);
      if (opts.filterTo !== undefined) {
        filter.frequency.exponentialRampToValueAtTime(Math.max(40, opts.filterTo), t0 + dur);
      }
      filter.Q.value = 1.2;
      node.connect(filter);
      node = filter;
    }
    node.connect(gain);
    gain.connect(bus);
    osc.start(t0);
    osc.stop(t0 + dur + 0.03);
  }

  private noise(opts: NoiseOptions): void {
    const ctx = this.ctx;
    const bus = this.sfxBus;
    const buffer = this.noiseBuffer;
    if (!ctx || !bus || !buffer) return;

    const t0 = ctx.currentTime + (opts.when ?? 0);
    const dur = Math.max(0.02, opts.dur);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = opts.type ?? 'lowpass';
    filter.frequency.setValueAtTime(Math.max(40, opts.filter ?? 1200), t0);
    if (opts.filterTo !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(40, opts.filterTo), t0 + dur);
    }
    filter.Q.value = opts.q ?? 0.8;

    const gain = ctx.createGain();
    const peak = Math.max(SILENT, opts.gain ?? 0.16);
    gain.gain.setValueAtTime(SILENT, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.015, dur * 0.25));
    gain.gain.exponentialRampToValueAtTime(SILENT, t0 + dur);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    src.start(t0);
    src.stop(t0 + dur + 0.03);
  }
}
